import { Button, Card, Description, Heading, useOverlayState } from '@heroui/react';
import { useMutation } from '@tanstack/react-query';
import {
  resolveMisuseReport,
  type RecentMisuseReport,
} from '../../../api/misuse-reports';
import { formatDateTimeBR } from '../../../utils/date-utils';
import { showErrorFromUnknown } from '../../../lib/toast';
import {
  AlreadyResolvedMisuseDialog,
  WillRemoveMisuseDialog,
} from './resolve-misuse-dialogs';
import { ExpandableImage } from '../../../components/ui/expandable-image';

interface MisuseReportAlertProps {
  report: RecentMisuseReport;
  isReporter?: boolean;
  onResolved?: () => void;
}

export function MisuseReportAlert({
  report,
  isReporter = false,
  onResolved,
}: MisuseReportAlertProps) {
  const alreadyResolvedDialog = useOverlayState();
  const willRemoveDialog = useOverlayState();

  const resolveMutation = useMutation({
    mutationFn: ({
      resolution,
      reason,
    }: {
      resolution: 'WILL_REMOVE' | 'ALREADY_RESOLVED';
      reason?: string;
    }) => resolveMisuseReport(report.id, resolution, reason),
    onSuccess: () => onResolved?.(),
    onError: showErrorFromUnknown,
  });

  const details = [
    report.spotCode ? `Vaga ${report.spotCode}` : null,
    report.licensePlate ? `Placa ${report.licensePlate}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Card className="border border-danger bg-danger/10">
        <Card.Content className="flex flex-col gap-4 p-5">
          <div className="flex items-start gap-3">
            <div
              className="flex size-11 shrink-0 items-center justify-center rounded-full bg-danger/15 text-xl leading-none"
              aria-hidden
            >
              🚨
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <Heading level={3} className="text-lg font-semibold leading-snug">
                Mau uso de vaga reportado
              </Heading>
              {details ? <p className="text-sm font-medium text-foreground">{details}</p> : null}
              <Description>Reportado em {formatDateTimeBR(report.createdAt)}</Description>
            </div>
          </div>

          {report.photoUrl ? (
            <ExpandableImage
              src={report.photoUrl}
              alt="Foto do mau uso reportado"
              className="max-h-36 w-full rounded-xl object-cover"
            />
          ) : null}

          {isReporter ? (
            <Button
              variant="secondary"
              size="lg"
              className="h-12 w-full"
              onPress={alreadyResolvedDialog.open}
            >
              Já resolvido
            </Button>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                variant="secondary"
                size="lg"
                className="h-12 w-full"
                onPress={willRemoveDialog.open}
              >
                OK, vou retirar
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-12 w-full"
                onPress={alreadyResolvedDialog.open}
              >
                Já resolvido
              </Button>
            </div>
          )}
        </Card.Content>
      </Card>

      <AlreadyResolvedMisuseDialog
        state={alreadyResolvedDialog}
        question={
          isReporter
            ? 'O carro já foi retirado da vaga?'
            : 'O carro já foi retirado da vaga por algum outro morador?'
        }
        onConfirm={() =>
          resolveMutation.mutateAsync({ resolution: 'ALREADY_RESOLVED' })
        }
      />

      {isReporter ? null : (
        <WillRemoveMisuseDialog
          state={willRemoveDialog}
          report={report}
          onConfirm={(reason) =>
            resolveMutation.mutateAsync({ resolution: 'WILL_REMOVE', reason })
          }
        />
      )}
    </>
  );
}
